import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineChartBar,
  HiOutlineArrowLeft,
  HiOutlineCalendar,
  HiOutlineArrowDownTray,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from "react-icons/hi2";
import {
  BarChart,
  LineChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import axios from "axios";
import ReportFilterBar from "../../components/ReportFilterBar";
import "../Reports.css";
import "./OeeSummaryReport.css";
import apiClient from "../../config/api";
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from "../../utils/reportGenerationToast";

import { SEMANTIC_SUCCESS_MID } from '../../theme/chartColors';
// ─── Date utilities ───────────────────────────────────────────────────────────
const buildDateRange = (startStr, endStr) => {
  if (!startStr || !endStr) return [];
  const dates = [];
  const cur = new Date(startStr);
  const end = new Date(endStr);
  while (cur <= end) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};
const dateLabel = (d) => d.getDate();
const dateTitle = (d) =>
  d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });

// ─── Only 4 key metric rows — Machine Metric Table removed ────────────────────
const ROW_DEFS = [
  { key: "oee", label: "OEE", type: "main", format: "pct" },
  { key: "availability", label: "A%", type: "main", format: "pct" },
  { key: "performance", label: "P%", type: "main", format: "pct" },
  { key: "quality", label: "Q%", type: "main", format: "pct" },
];

// ─── OEE colour helper ────────────────────────────────────────────────────────
const oeeClass = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (n > 100) return "osr-exceed";
  if (n >= 85) return "osr-green";
  if (n >= 65) return "osr-amber";
  return "osr-red";
};

const fmt = (v, format) => {
  if (v === null || v === undefined || v === "") return "–";
  const n = Number(v);
  if (isNaN(n)) return "–";
  if (format === "pct") {
    if (n < 0) return "0.0%";
    return n.toFixed(1) + "%";
  }
  return n.toFixed(1);
};

// ─── Safe average: skip zero-data days ───────────────────────────────────────
const avgOf = (arr, key) => {
  const valid = arr.filter((d) => d._hasData);
  if (!valid.length) return null;
  const sum = valid.reduce((s, d) => s + (d[key] ?? 0), 0);
  return Math.round((sum / valid.length) * 10) / 10;
};

// ─── Correct OEE from A × P × Q ──────────────────────────────────────────────
const deriveOee = (a, p, q) => {
  if (a == null || p == null || q == null) return null;
  return Math.round((a * p * q) / 10000 * 10) / 10;
};

// ─── Chart component ──────────────────────────────────────────────────────────
const ChartView = ({ dates, rows, chartType = "bar" }) => {
  const [hiddenKeys, setHiddenKeys] = useState(new Set());

  React.useEffect(() => {
    setHiddenKeys(new Set());
  }, [chartType]);

  const chartData = dates.map((date, idx) => {
    const dayData = { day: `${dateLabel(date)}`, date: dateTitle(date) };
    const validRows = rows.filter((r) => r.daily[idx]?._hasData);
    const count = validRows.length || 1;
    const sum = (key) =>
      validRows.reduce((s, r) => s + (r.daily[idx]?.[key] ?? 0), 0);
    dayData["A%"] = Math.round((sum("availability") / count) * 10) / 10;
    dayData["P%"] = Math.round((sum("performance") / count) * 10) / 10;
    dayData["Q%"] = Math.round((sum("quality") / count) * 10) / 10;
    dayData["OEE%"] = Math.round(
      (deriveOee(dayData["A%"], dayData["P%"], dayData["Q%"]) ?? 0) * 10
    ) / 10;
    return dayData;
  });

  const colors = {
    "A%": "#3b82f6",
    "P%": SEMANTIC_SUCCESS_MID,
    "Q%": "#f59e0b",
    "OEE%": "#8b5cf6",
  };

  const toggleKey = (key) =>
    setHiddenKeys((prev) => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });

  const legendItems = [
    { dataKey: "A%", label: "Availability %", color: colors["A%"] },
    { dataKey: "P%", label: "Performance %", color: colors["P%"] },
    { dataKey: "Q%", label: "Quality %", color: colors["Q%"] },
    { dataKey: "OEE%", label: "OEE %", color: colors["OEE%"] },
  ];

  const CustomLegend = () => (
    <div style={{ display: "flex", gap: 16, padding: "12px 0", flexWrap: "wrap" }}>
      {legendItems.map((item) => (
        <div
          key={item.dataKey}
          onClick={() => toggleKey(item.dataKey)}
          style={{
            display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
            padding: "4px 10px", borderRadius: 6, transition: "all 0.2s",
            backgroundColor: hiddenKeys.has(item.dataKey) ? "#f3f4f6" : "transparent",
            border: "1px solid",
            borderColor: hiddenKeys.has(item.dataKey) ? "#e5e7eb; border-color: var(--border-primary)" : "transparent",
          }}
        >
          <div style={{
            width: 10, height: 10, borderRadius: 2,
            backgroundColor: item.color,
            opacity: hiddenKeys.has(item.dataKey) ? 0.3 : 1,
          }} />
          <span style={{
            fontSize: 13, color: hiddenKeys.has(item.dataKey) ? "#9ca3af" : "#374151",
            textDecoration: hiddenKeys.has(item.dataKey) ? "line-through" : "none",
          }}>
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{
        backgroundColor: "#1e293b", padding: "10px 14px",
        border: "1px solid #334155", borderRadius: 8, minWidth: 160,
        boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
      }}>
        <p style={{ margin: "0 0 8px 0", fontSize: 12, fontWeight: 700, color: "#94a3b8" }}>
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry) => (
          <p key={entry.name} style={{ margin: "3px 0", fontSize: 13, color: entry.color, fontWeight: 600 }}>
            {entry.name}: {entry.value}%
          </p>
        ))}
      </div>
    );
  };

  const ChartElement = chartType === "bar" ? BarChart : LineChart;

  return (
    <div className="oee-chart-container">
      <CustomLegend />
      <ResponsiveContainer width="100%" height={420}>
        <ChartElement data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 50 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="day"
            angle={-40}
            textAnchor="end"
            height={70}
            tick={{ fontSize: 12, fill: "#6b7280" }}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 12, fill: "#6b7280" }}
            label={{ value: "%", angle: -90, position: "insideLeft", offset: 10, fill: "#9ca3af" }}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={85} stroke="#22c55e" strokeDasharray="4 2" label={{ value: "85%", position: "right", fontSize: 11, fill: "#22c55e" }} />
          {legendItems.map(({ dataKey, label, color }) =>
            !hiddenKeys.has(dataKey) ? (
              chartType === "bar" ? (
                <Bar key={dataKey} dataKey={dataKey} fill={color} name={label} radius={[4, 4, 0, 0]} maxBarSize={40} />
              ) : (
                <Line
                  key={dataKey}
                  type="monotone"
                  dataKey={dataKey}
                  stroke={color}
                  name={label}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: color }}
                  activeDot={{ r: 6 }}
                />
              )
            ) : null
          )}
        </ChartElement>
      </ResponsiveContainer>
    </div>
  );
};

// ─── Summary Table ────────────────────────────────────────────────────────────
const SummaryTable = ({ dates, rows }) => {
  const [frozenCols, setFrozenCols] = useState(2);

  const FREEZE_OPTIONS = [
    { label: "None", value: 0 },
    { label: "Machine", value: 1 },
    { label: "+ Metric", value: 2 },
  ];

  const COL_LEFT = [0, 180];
  const BG_HEADER = "#1e293b";
  const BG_LEGEND = "#ffffff";
  const BG_CARD = "#ffffff";

  const COL_W = [180, 150];

  const sticky = (colIdx, bg = BG_CARD) => {
    const isFrozen = frozenCols >= colIdx;
    const widthStyle = {
      minWidth: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
      width: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
      maxWidth: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
      boxSizing: 'border-box'
    };
    if (!isFrozen) return widthStyle;
    return { ...widthStyle, position: "sticky", left: COL_LEFT[colIdx - 1], zIndex: 6 - colIdx, background: bg };
  };

  const thStyle = (colIdx) => ({
    position: "sticky",
    top: 0,
    ...(frozenCols >= colIdx ? { left: COL_LEFT[colIdx - 1] } : {}),
    zIndex: frozenCols >= colIdx ? 22 - colIdx : 11,
    background: BG_HEADER,
    color: "#fff",
    minWidth: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
    width: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
    maxWidth: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
    boxSizing: 'border-box'
  });

  const shiftThStyle = () => ({
    position: "sticky", top: 0, zIndex: 10,
    background: BG_HEADER, color: "#fff",
  });

  const legendStyle = (frozen, colIdx = 1) => ({
    position: "sticky", top: 0,
    ...(frozen ? { left: COL_LEFT[colIdx - 1] } : {}),
    zIndex: frozen ? 14 - colIdx : 10,
    background: BG_LEGEND,
  });

  const TOTAL_ROWS = ROW_DEFS.length;

  return (
    <div className="osr-outer">
      {/* Toolbar */}
      <div className="lrt-toolbar">
        <span className="lrt-toolbar-label">📌 Freeze columns:</span>
        <div className="lrt-freeze-btns">
          {FREEZE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`lrt-freeze-btn${frozenCols === opt.value ? " active" : ""}`}
              onClick={() => setFrozenCols(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="osr-legend-chips">
          <span className="osr-chip osr-chip-exceed">{">"} 100% (Anomaly)</span>
          <span className="osr-chip osr-chip-green">OEE/A/P/Q ≥ 85%</span>
          <span className="osr-chip osr-chip-amber">65 – 84%</span>
          <span className="osr-chip osr-chip-red">Below 65%</span>
        </span>
      </div>

      <div className="lrt-wrapper">
        <table className="lrt-table osr-table">
          <thead>
            {/* Legend row */}
            <tr>
              <td className="lrt-legend" style={{ ...legendStyle(frozenCols >= 1), borderRight: 'none' }}>
                <strong>A%</strong> Availability &nbsp;
                <strong>P%</strong> Performance &nbsp;
              </td>
              <td className="lrt-legend" style={{ ...legendStyle(frozenCols >= 2, 2), paddingLeft: 0 }}>
                <strong>Q%</strong> Quality &nbsp;
                <strong>OEE</strong> = A × P × Q
              </td>
              {dates.map((d, i) => (
                <td
                  key={i}
                  className="lrt-shift-header"
                  title={dateTitle(d)}
                  style={{ position: "sticky", top: 0, zIndex: 10, background: BG_LEGEND }}
                >
                  {dateLabel(d)}
                </td>
              ))}
              <td
                className="lrt-shift-header osr-avg-header"
                style={{ position: "sticky", top: 0, zIndex: 10, background: BG_LEGEND }}
              >
                Avg
              </td>
            </tr>
            {/* Column headers */}
            <tr>
              <th className="lrt-th osr-th-machine" style={thStyle(1)}>Machine</th>
              <th className="lrt-th osr-th-metric" style={thStyle(2)}>Metric</th>
              {dates.map((_, i) => (
                <th key={i} className="lrt-th lrt-ot" style={shiftThStyle()}>{i + 1}</th>
              ))}
              <th className="lrt-th osr-th-avg" style={shiftThStyle()}>Avg</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, rIdx) => (
              <React.Fragment key={rIdx}>
                {ROW_DEFS.map((def, dIdx) => {
                  const isFirstRow = dIdx === 0;
                  const isOee = def.key === "oee";

                  return (
                    <tr
                      key={def.key}
                      className={[
                        "osr-row-main",
                        isOee ? "osr-row-oee" : "",
                        rIdx % 2 === 0 ? "osr-even-mach" : "",
                      ].join(" ")}
                    >
                      {/* Machine cell spanning all rows */}
                      {isFirstRow && (
                        <td
                          className="osr-cell osr-machine"
                          rowSpan={TOTAL_ROWS}
                          style={sticky(1, "var(--bg-secondary, #f8fafc)")}
                        >
                          {row.label}
                        </td>
                      )}

                      {/* Metric label */}
                      <td
                        className={`osr-cell osr-metric-label osr-metric-main${isOee ? " osr-metric-oee" : ""}`}
                        style={sticky(2, isOee ? "#eef0ff" : "var(--bg-secondary, #f8fafc)")}
                      >
                        {def.label}
                      </td>

                      {/* Daily values */}
                      {row.daily.map((dv, di) => {
                        const val = dv._hasData ? dv[def.key] : null;
                        const color = def.format === "pct" && val != null ? oeeClass(val) : "";
                        return (
                          <td key={di} className={`osr-cell osr-val ${color}`}>
                            {val != null ? fmt(val, def.format) : <span style={{ color: "#cbd5e1" }}>–</span>}
                          </td>
                        );
                      })}

                      {/* Average */}
                      <td className={`osr-cell osr-avg${def.format === "pct" && row.averages[def.key] != null ? " " + oeeClass(row.averages[def.key]) : ""}`}>
                        {row.averages[def.key] != null ? fmt(row.averages[def.key], def.format) : "–"}
                      </td>
                    </tr>
                  );
                })}

                {/* Spacer between machines */}
                <tr className="lrt-row-spacer">
                  <td colSpan={2 + dates.length + 1} />
                </tr>
              </React.Fragment>
            ))}

            {/* Grand Average removed */}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Page component ───────────────────────────────────────────────────────────
const OeeSummaryReport = () => {
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState("");
  const [viewType, setViewType] = useState("table");
  const [chartType, setChartType] = useState("bar");
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const activeFilterCount =
    selectedPlants.length + selectedLines.length + selectedMachines.length +
    selectedShifts.length + selectedProducts.length + selectedSkus.length +
    (startDate ? 1 : 0) + (endDate ? 1 : 0);

  // ── Generate report ────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError("Please select both Start Date and End Date.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start Date must be before End Date.");
      return;
    }
    setError("");
    showReportGeneratingToast();
    setLoading(true);

    try {
      const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = {
        startDate,
        endDate,
        ...(selectedPlants?.length > 0 && { plantId: selectedPlants[0] }),
        ...(selectedLines?.length > 0 && { lineId: selectedLines[0] }),
        ...(selectedMachines?.length > 0 && { machineId: selectedMachines[0] }),
        ...(selectedShifts?.length > 0 && { shift: selectedShifts[0] }),
        ...(selectedProducts?.length > 0 && { productId: selectedProducts[0] }),
        ...(selectedSkus?.length > 0 && { skuId: selectedSkus[0] }),
      };

      const response = await apiClient.get("/reports/oee-summary", { params, headers });
      setReportData(response.data);
      reportGenerationDone();
    } catch (err) {
      console.error(err);
      const msg = "Failed to fetch report data. Please check your connection and try again.";
      setError(msg);
      reportGenerationFailed(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Process API data into table structure ──────────────────────────────────
  const processedData = React.useMemo(() => {
    if (!reportData || !Array.isArray(reportData)) return null;

    const dates = buildDateRange(startDate, endDate);
    if (reportData.length === 0) {
      return { dates, rows: [], grand: {}, isEmpty: true };
    }

    const dateStrings = dates.map((d) => {
      // Use local date string to match backend's date field (yyyy-MM-dd)
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    });

    // Group by machine label
    const rowDict = {};
    reportData.forEach((item) => {
      const plant = item.plantName || "Unknown Plant";
      const line = item.lineName || "Unknown Line";
      const machine = item.machineName || "Unknown Machine";
      const label = `${plant} → ${line} → ${machine}`;

      if (!rowDict[label]) rowDict[label] = {};

      // Key by date (API returns yyyy-MM-dd)
      const dateKey = (item.date || "").split("T")[0];

      // Aggregate multiple shifts for same machine+date
      if (!rowDict[label][dateKey]) {
        rowDict[label][dateKey] = {
          _items: [],
        };
      }
      rowDict[label][dateKey]._items.push(item);
    });

    const rows = Object.keys(rowDict).map((label) => {
      const machineDates = rowDict[label];

      const daily = dateStrings.map((dateStr) => {
        if (!machineDates[dateStr]) {
          return { _hasData: false, oee: null, availability: null, performance: null, quality: null };
        }
        const items = machineDates[dateStr]._items;
        // Aggregate: weighted average by planned production time, or simple avg
        const totalPPT = items.reduce((s, i) => s + (i.plannedProductionTime || 0), 0);

        const wavg = (key) => {
          if (totalPPT === 0) {
            // fallback: simple average
            const vals = items.map((i) => i[key] ?? 0);
            return vals.reduce((s, v) => s + v, 0) / vals.length;
          }
          return items.reduce((s, i) => s + ((i[key] ?? 0) * (i.plannedProductionTime || 0)), 0) / totalPPT;
        };

        const a = Math.round(wavg("availability") * 10) / 10;
        const p = Math.round(wavg("performance") * 10) / 10;
        const q = Math.round(wavg("quality") * 10) / 10;
        // OEE is always derived from A×P×Q to ensure consistency
        const oee = deriveOee(a, p, q);

        return {
          _hasData: true,
          availability: a,
          performance: p,
          quality: q,
          oee,
        };
      });

      // Averages over days that actually have data
      const averages = {};
      ROW_DEFS.forEach(({ key }) => {
        averages[key] = avgOf(daily, key);
      });
      // Ensure OEE average is also A×P×Q derived, not average of averages
      if (averages.availability != null && averages.performance != null && averages.quality != null) {
        averages.oee = deriveOee(averages.availability, averages.performance, averages.quality);
      }

      return { label, daily, averages };
    });

    return { dates, rows, isEmpty: false };
  }, [reportData, startDate, endDate]);

  const clearAllFilters = () => {
    setSelectedPlants([]);
    setSelectedLines([]);
    setSelectedMachines([]);
    setSelectedShifts([]);
    setSelectedProducts([]);
    setSelectedSkus([]);
    setStartDate("");
    setEndDate("");
    setReportData(null);
    setError("");
  };

  // ── Export to Excel ─────────────────────────────────────────────────────────
  const handleExport = async () => {
    if (!processedData) return;
    const { dates, rows } = processedData;

    const wb = new ExcelJS.Workbook();
    wb.creator = "MaX";
    const ws = wb.addWorksheet("OEE Summary");

    const PURPLE = { type: "pattern", pattern: "solid", fgColor: { argb: "FF667EEA" } };
    const LGRAY = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8FAFC" } };
    const GREEN = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD1FAE5" } };
    const AMBER = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEF3C7" } };
    const RED = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEE2E2" } };
    const border = {
      top: { style: "thin", color: { argb: "FFE2E8F0" } },
      left: { style: "thin", color: { argb: "FFE2E8F0" } },
      bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
      right: { style: "thin", color: { argb: "FFE2E8F0" } },
    };
    const center = { horizontal: "center", vertical: "middle" };
    const left = { horizontal: "left", vertical: "middle" };

    ws.columns = [
      { width: 34 },
      { width: 14 },
      ...dates.map(() => ({ width: 7 })),
      { width: 8 },
    ];

    const hdr = ws.addRow(["Machine", "Metric", ...dates.map((d) => dateLabel(d)), "Avg"]);
    hdr.height = 20;
    hdr.eachCell((c) => {
      c.fill = PURPLE; c.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
      c.border = border; c.alignment = center;
    });
    hdr.getCell(1).alignment = left;
    hdr.getCell(2).alignment = left;

    rows.forEach((row) => {
      const machStart = ws.rowCount + 1;
      ROW_DEFS.forEach((def, dIdx) => {
        const rowData = [
          dIdx === 0 ? row.label : "",
          def.label,
          ...row.daily.map((dv) => dv._hasData ? (dv[def.key] ?? "") : ""),
          row.averages[def.key] ?? "",
        ];
        const exRow = ws.addRow(rowData);
        exRow.height = 15;
        exRow.eachCell((c) => {
          c.border = border; c.font = { size: 9 }; c.alignment = center; c.fill = LGRAY;
        });
        exRow.getCell(1).alignment = left;
        exRow.getCell(2).alignment = left;
        if (def.format === "pct") {
          row.daily.forEach((dv, i) => {
            if (!dv._hasData) return;
            const v = dv[def.key];
            if (v == null) return;
            exRow.getCell(3 + i).fill = v >= 85 ? GREEN : v >= 65 ? AMBER : RED;
          });
          const avg = row.averages[def.key];
          if (avg != null)
            exRow.getCell(3 + dates.length).fill = avg >= 85 ? GREEN : avg >= 65 ? AMBER : RED;
        }
      });

      const machEnd = ws.rowCount;
      if (machEnd > machStart) {
        ws.mergeCells(machStart, 1, machEnd, 1);
        ws.getCell(machStart, 1).alignment = { vertical: "top", wrapText: true };
      }
      ws.addRow([]);
    });

    ws.views = [{ state: "frozen", xSplit: 2, ySplit: 1, topLeftCell: "C2" }];

    const fromStr = new Date(startDate).toLocaleDateString("en-GB").replace(/\//g, "-");
    const toStr = new Date(endDate).toLocaleDateString("en-GB").replace(/\//g, "-");
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `OEE_Summary_${fromStr}_to_${toStr}.xlsx`);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="pagecontainer report-page">
      {/* Header */}
      <div className="page-header">
        <div className="oee-report-header-left">
          <button
            className="oee-report-back-btn mr-2"
            onClick={() => navigate("/home/reports")}
            title="Back"
          >
            <HiOutlineArrowLeft size={18} />
          </button>
          <div>
            <h1 className="oee-report-title">
              <HiOutlineChartBar /> OEE Summary Report
            </h1>
            <p className="oee-report-subtitle">
              Availability · Performance · Quality · OEE per machine per day
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="oee-filter-toggle-btn"
            onClick={() => setShowFilters((p) => !p)}
          >
            <HiOutlineAdjustmentsHorizontal size={16} />
            Filters
            {activeFilterCount > 0 && (
              <span className="oee-filter-badge">{activeFilterCount}</span>
            )}
            {showFilters ? <HiOutlineChevronUp size={13} /> : <HiOutlineChevronDown size={13} />}
          </button>
          <button
            className="btn-primary btn"
            onClick={handleExport}
            disabled={!reportData}
            title={reportData ? "Export to Excel" : "Generate a report first"}
          >
            <HiOutlineArrowDownTray /> Export
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="reportbox searchfilterbox">
          <div className="report-filters">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                className={`filter-input${error ? " input-error" : ""}`}
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setError(""); }}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                className={`filter-input${error ? " input-error" : ""}`}
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setError(""); }}
              />
            </div>
            <ReportFilterBar
              selectedPlants={selectedPlants} onPlantChange={setSelectedPlants}
              selectedLines={selectedLines} onLineChange={setSelectedLines}
              selectedMachines={selectedMachines} onMachineChange={setSelectedMachines}
              selectedShifts={selectedShifts} onShiftChange={setSelectedShifts}
              selectedProducts={selectedProducts} onProductChange={setSelectedProducts}
              selectedSkus={selectedSkus} onSkuChange={setSelectedSkus}
            />
          </div>

          <div className="report-filter-actions">
            {activeFilterCount > 0 && (
              <button className="oee-clear-filters-btn" onClick={clearAllFilters}>
                ✕ Clear Filters
              </button>
            )}
            <button type="button" className="btn-primary" onClick={handleGenerate} disabled={loading}>
              <HiOutlineCalendar /> Generate Report
            </button>
          </div>
          {error && <p className="report-filter-error">{error}</p>}
        </div>
      )}

      {/* Report content */}
      {reportData ? (
        <>
          {/* View type switcher */}
          <div className="viewTypeheader">
            <div style={{ padding: "5px 24px" }}>
              <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <label style={{ fontSize: 14, fontWeight: 600, color: "#1f2937" }}>View Type:</label>
                  {["table", "chart"].map((vt) => (
                    <button
                      key={vt}
                      onClick={() => setViewType(vt)}
                      className={viewType === vt ? "btn btn-primary" : "btn btn-secondary"}
                    // style={{
                    //   padding: "6px 16px", borderRadius: 6, border: "1px solid #e5e7eb; border-color: var(--border-primary)",
                    //   backgroundColor: viewType === vt ? "#667eea" : "#fff",
                    //   color: viewType === vt ? "#fff" : "#1f2937",
                    //   fontSize: 13, fontWeight: 500, cursor: "pointer", transition: "all 0.2s",
                    // }}
                    >
                      {vt === "table" ? "Table View" : "Chart View"}
                    </button>
                  ))}
                </div>

                {viewType === "chart" && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <label style={{ fontSize: 14, fontWeight: 600, color: "#1f2937" }}>Chart Type:</label>
                    {["bar", "line"].map((ct) => (
                      <button
                        key={ct}
                        onClick={() => setChartType(ct)}
                        className={chartType === ct ? "btn btn-primary" : "btn btn-secondary"}
                      // style={{
                      //   padding: "6px 16px", borderRadius: 6, border: "1px solid #e5e7eb; border-color: var(--border-primary)",
                      //   backgroundColor: chartType === ct ? "#667eea" : "#fff",
                      //   color: chartType === ct ? "#fff" : "#1f2937",
                      //   fontSize: 13, fontWeight: 500, cursor: "pointer", transition: "all 0.2s",
                      // }}
                      >
                        {ct === "bar" ? "Bar Chart" : "Line Chart"}
                      </button>
                    ))}
                  </div>
                )}

                {/* Live summary pills */}
                {processedData && !processedData.isEmpty && (
                  <div style={{ display: "flex", gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
                    {[
                      { label: "Machines", value: processedData.rows.length, color: "#6366f1" },
                      { label: "Days", value: processedData.dates.length, color: "#0ea5e9" },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "4px 12px", borderRadius: 20,
                        backgroundColor: color + "15", border: `1px solid ${color}30`,
                      }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color }}>{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Empty state */}
          {processedData?.isEmpty && (
            <div style={{ padding: "60px 40px", textAlign: "center", backgroundColor: "#f8fafc", borderRadius: 8, margin: 20 }}>
              <HiOutlineChartBar style={{ fontSize: 48, color: "#94a3b8", marginBottom: 16 }} />
              <h3 style={{ fontSize: 18, color: "#334155", margin: "0 0 8px 0" }}>No Data Available</h3>
              <p style={{ color: "#64748b", margin: 0 }}>
                There are no production records for the selected filters and date range.
              </p>
            </div>
          )}

          {/* Table view */}
          {!processedData?.isEmpty && viewType === "table" && (
            <SummaryTable
              dates={processedData.dates}
              rows={processedData.rows}
            />
          )}

          {/* Chart view */}
          {!processedData?.isEmpty && viewType === "chart" && (
            <ChartView
              dates={processedData.dates}
              rows={processedData.rows}
              chartType={chartType}
            />
          )}
        </>
      ) : (
        <div className="report-container">
          <div className="report-content">
            <div className="report-placeholder">
              <HiOutlineChartBar className="placeholder-icon" />
              <h3>OEE Summary Report</h3>
              <p>Select a date range and click "Generate Report" to view data</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OeeSummaryReport;
